import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import _ from 'lodash';
import moment from 'moment';
import {DatePicker, FormError, FormLayout, NumberInput, ObjectSelect2, TextArea, TimeInput, Toggle, WeekdaySelector} from 'shared/components';
import {Modal, ModalButtons} from 'shared/components/modals';
import {t} from 'shared/core';

const DimensionValueSelect = observer(({uiState, dimension}) => {
  const {editingShift} = uiState;
  const selectedValue = _.find(dimension.values, {id: editingShift.dimensionValueIdFor(dimension)});
  const values = _.unionBy(uiState.applicableDimensionValues(dimension, editingShift.employeeId), _.compact([selectedValue]), 'id');

  return (
    <ObjectSelect2
      label={dimension.name}
      translateLabel={false}
      allowClear
      value={selectedValue}
      items={values}
      onChange={value => editingShift.setDimensionValue(dimension, value)}
    />
  );
});

const ShiftTimes = observer(({uiState}) => {
  const {editingShift, errors} = uiState;

  return (
    <FormLayout.Group>
      <TimeInput
        label='time_tracking.shifts.Start Time'
        initialValue={editingShift.startTimeString}
        errorMessage={errors.startTime}
        onFormat={({value, error}) => !error && editingShift.merge({startTimeString: value})}
      />
      <TimeInput
        label='time_tracking.shifts.End Time'
        initialValue={editingShift.endTimeString}
        lowerBound={editingShift.startTimeString}
        errorMessage={errors.endTime}
        onFormat={({value, error}) => !error && editingShift.merge({endTimeString: value})}
      />
    </FormLayout.Group>
  );
});

const RepeatSettings = observer(({uiState}) => {
  const {editingShift, errors} = uiState;
  const {scheduleRule} = editingShift;

  return (
    <div>
      <div className='flex align-items-middle'>
        <Toggle enabled={!!scheduleRule} onChange={enabled => uiState.setRepeatEnabled(enabled)}/>
        <span className='pl2'><FormattedMessage id='time_tracking.shifts.Repeat Shift'/></span>
      </div>
      {scheduleRule && <div className='ShiftRepeatSettings'>
        <WeekdaySelector
          label={t('time_tracking.shifts.Repeats On')}
          selectedDays={scheduleRule.repeatDays}
          onChange={repeatDays => scheduleRule.repeatDays = repeatDays}
          errorMessage={errors.scheduleRule?.repeatDays}
          weekStart={0}
        />
        <DatePicker
          label='time_tracking.shifts.Repeat Until'
          value={scheduleRule.repeatEndsOn}
          errorMessage={errors.scheduleRule?.repeatEndsOn}
          helpMessage={t('time_tracking.shifts.REPEAT_UNTIL_HELP')}
          options={{startDate: editingShift.date, endDate: moment(editingShift.date).add(1, 'year').toDate()}}
          onChange={date => uiState.setRepeatEndsOn(date)}
        />
      </div>}
    </div>
  );
});

const ShiftDescription = observer(({uiState}) => {
  const {editingShift} = uiState;

  return (
    <FormattedMessage
      id='time_tracking.shifts.SHIFT_DESCRIPTION'
      values={{
        date: moment(editingShift.date).format('dddd, MMMM D'),
        assignee: editingShift.employee ? editingShift.employee.name : t('time_tracking.shifts.Unassigned Shifts')
      }}
    />
  );
});

const EditShiftModal = observer(({uiState, isOpen}) => {
  const {editingShift, errors} = uiState;

  return (
    <Modal
      isOpen={isOpen}
      onHide={() => uiState.closeEditShiftModal()}
      size='md'
      title={editingShift.isNew ? 'time_tracking.shifts.Add Shift' : 'time_tracking.shifts.Edit Shift'}
    >
      <div className='jumbo mb2'><ShiftDescription uiState={uiState}/></div>
      <FormLayout>
        <ShiftTimes uiState={uiState}/>
        {uiState.sortedDimensions.map(dimension => <DimensionValueSelect key={dimension.id} uiState={uiState} dimension={dimension}/>)}
        <NumberInput
          label='time_tracking.shifts.Unpaid Break'
          value={editingShift.unpaidBreakMinutes}
          errorMessage={errors.unpaidBreakMinutes}
          onChange={e => editingShift.merge({unpaidBreakMinutes: e.target.value})}
          connectedRight={
            <div className='InputGroup'>
              <span className='InputGroup-tag small'><FormattedMessage id='time_tracking.shifts.minutes'/></span>
            </div>
          }
        />
        <TextArea
          label='time_tracking.shifts.Notes'
          value={editingShift.note}
          onChange={e => editingShift.merge({note: e.target.value})}
        />
        <RepeatSettings uiState={uiState}/>
      </FormLayout>
      <FormError message={errors.base}/>
      {uiState.schedule.sendSchedulePublishedNotification && uiState.originalShift?.published && uiState.originalShift.employee &&
        <div className='jumbo mb2'>
          <FormattedMessage
            id='time_tracking.shifts.PUBLISHED_SHIFT_UPDATED_EMAIL_NOTICE'
            values={{name: uiState.originalShift.employee.name}}
          />
        </div>
      }
      <ModalButtons onSave={() => uiState.saveShift()} onCancel={() => uiState.closeEditShiftModal()}/>
    </Modal>
  );
});

export default EditShiftModal;
